/** FNV-1a over UTF-8. Pure integer math, identical in every runtime (and in Go). */
export function utf8(str: string): Uint8Array {
  const out: number[] = [];
  for (let i = 0; i < str.length; i++) {
    let c = str.charCodeAt(i);
    if (c >= 0xd800 && c <= 0xdbff && i + 1 < str.length) {
      const d = str.charCodeAt(i + 1);
      if (d >= 0xdc00 && d <= 0xdfff) {
        c = 0x10000 + ((c - 0xd800) << 10) + (d - 0xdc00);
        i++;
      }
    }
    if (c < 0x80) out.push(c);
    else if (c < 0x800) out.push(0xc0 | (c >> 6), 0x80 | (c & 63));
    else if (c < 0x10000) out.push(0xe0 | (c >> 12), 0x80 | ((c >> 6) & 63), 0x80 | (c & 63));
    else out.push(0xf0 | (c >> 18), 0x80 | ((c >> 12) & 63), 0x80 | ((c >> 6) & 63), 0x80 | (c & 63));
  }
  return Uint8Array.from(out);
}

export function fnv1a32Bytes(bytes: ArrayLike<number>, seed = 0x811c9dc5): number {
  let h = seed >>> 0;
  for (let i = 0; i < bytes.length; i++) {
    h ^= bytes[i]! & 0xff;
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

export function fnv1a32(str: string, seed = 0x811c9dc5): number {
  return fnv1a32Bytes(utf8(str), seed);
}

/** 64 bits of hash as 16 hex chars (two independent FNV lanes). */
export function hash64Hex(str: string): string {
  const b = utf8(str);
  const a = fnv1a32Bytes(b, 0x811c9dc5);
  const c = fnv1a32Bytes(b, 0x2c9277b5);
  return a.toString(16).padStart(8, '0') + c.toString(16).padStart(8, '0');
}

/** Incremental hasher over integers for state hashing. */
export class Hasher {
  h = 0x811c9dc5;
  int(v: number): this {
    let x = v | 0;
    for (let i = 0; i < 4; i++) {
      this.h ^= x & 0xff;
      this.h = Math.imul(this.h, 0x01000193);
      x >>>= 8;
    }
    return this;
  }
  ints(arr: ArrayLike<number>, len = arr.length): this {
    this.int(len);
    for (let i = 0; i < len; i++) this.int(arr[i]!);
    return this;
  }
  hex(): string {
    return (this.h >>> 0).toString(16).padStart(8, '0');
  }
}

/** Canonical JSON: sorted keys, no whitespace. Used for level hashes. */
export function canonicalJson(v: unknown): string {
  if (v === null || typeof v !== 'object') return JSON.stringify(v);
  if (Array.isArray(v)) return '[' + v.map(canonicalJson).join(',') + ']';
  const o = v as Record<string, unknown>;
  const keys = Object.keys(o)
    .filter((k) => o[k] !== undefined)
    .sort();
  return '{' + keys.map((k) => JSON.stringify(k) + ':' + canonicalJson(o[k])).join(',') + '}';
}
